// ── Bankköltség + FX-analitika chartok (Recharts) ──────────────────────────
// A Statisztika → Bankköltség és Deviza (FX) fülek chartjai. A korábbi kézi
// SVG-implementáció (misc.js fxAxisFrame/FxStackChart/…) hűséges migrációja:
// a bevált kódolások (stack-színek, sraffozott "becsült" sáv, medián/P90/max
// referenciavonalak, break-even törtvonal) változatlanok, a keret a közös
// ChartFrame-ből jön (tengely-tipó, grid, legend, tooltip – egy rendszer).
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, LabelList, ReferenceLine,
  LineChart, Line, ComposedChart, Scatter,
} from 'recharts';
import { huFmt } from '../../utils';
import { axisTick, gridProps, legendProps, barCursor } from './ChartFrame';

// Kompakt tengely-számok (a régi fxKfmt): 1 250 000 → "1.3M" · 42 000 → "42k"
export const kFmt = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M`
  : Math.abs(v) >= 1e3 ? `${Math.round(v / 1e3)}k` : String(Math.round(v)));

const HU_MONTHS_LONG = ['január', 'február', 'március', 'április', 'május', 'június',
                        'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
const longMonth = (m) => {
  const i = parseInt(String(m).slice(5, 7), 10) - 1;
  return HU_MONTHS_LONG[i] ? `${String(m).slice(0, 4)}. ${HU_MONTHS_LONG[i]}` : m;
};
const shortMonth = (m) => String(m || '').slice(2);

// Nevezett tengelycím (a mockupon jóváhagyott elrendezés – bal felső sarok).
function AxisTitle({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#c2cddb',
      fontFamily: 'var(--mono)', marginBottom: 4 }}>{children}</div>
  );
}

const EMPTY = (text) => (
  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '1rem' }}>{text}</div>
);

// ── CcyTooltip ──────────────────────────────────────────────────────────────
// A ChartFrame MoneyTooltip devizatudatos párja: HUF → "Ft" 0 tizedessel,
// más deviza → 2 tizedes + devizakód. `colorMap` a sraffozott (pattern-fillű)
// sávok kulcs-négyzetét adja vissza tömör színnel.
function CcyTooltip({ active, payload, label, labelFormatter, currency = 'HUF',
  colorMap = {}, signed = false }) {
  if (!active || !payload || !payload.length) return null;
  const unit = currency === 'HUF' ? 'Ft' : currency;
  const dec = currency === 'HUF' ? 0 : 2;
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '0.55rem 0.75rem',
      boxShadow: 'var(--shadow)', fontSize: 'var(--fs-xs)', minWidth: 160,
    }}>
      <div style={{ color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0,
            background: colorMap[entry.dataKey] || entry.color || entry.fill || 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text)' }}>{entry.name}</span>
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', color: 'var(--text)',
            fontVariantNumeric: 'tabular-nums', paddingLeft: 12 }}>
            {signed && entry.value > 0 ? '+' : ''}{huFmt(entry.value, dec)} {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

// Árfolyam-tooltip (nem pénzösszeg): érték 2 tizedessel, egység nélkül.
function RateTooltip({ active, payload, label, colorMap = {} }) {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((e) => e.value != null);
  if (!rows.length) return null;
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '0.55rem 0.75rem',
      boxShadow: 'var(--shadow)', fontSize: 'var(--fs-xs)', minWidth: 160,
    }}>
      <div style={{ color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>{longMonth(label)}</div>
      {rows.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0,
            background: colorMap[entry.dataKey] || entry.color || 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text)' }}>{entry.name}</span>
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', color: 'var(--text)',
            fontVariantNumeric: 'tabular-nums', paddingLeft: 12 }}>
            {huFmt(entry.value, 2)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Havi stacked oszlopdiagram ──────────────────────────────────────────────
// rows: [{ month:'YYYY-MM', <key>: érték, … }]
// series: [{ key, name, color, hatch? }] – hatch=true → sraffozott kitöltés
// (kötelező kódolás: a "becsült/következtetett" szegmens sraffozott, a
// detektált tömör). currency: HUF → "Ft", más deviza → 2 tizedes.
export function StackedMonthlyChart({ rows = [], series = [], currency = 'HUF',
  ytitle, height = 250, emptyText = 'Nincs adat.', showLegend = false }) {
  if (!rows.length) return EMPTY(emptyText);
  const colorMap = Object.fromEntries(series.map((s) => [s.key, s.color]));
  return (
    <div>
      {ytitle && <AxisTitle>{ytitle}</AxisTitle>}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} barCategoryGap="34%" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {/* Sraffozott minta a "becsült" szegmenshez – a Recharts a nyers SVG
              gyerekeket (defs/pattern) változtatás nélkül rendereli. */}
          {series.filter((s) => s.hatch).map((s) => (
            <defs key={s.key}>
              <pattern id={`fxhatch-${s.key}`} patternUnits="userSpaceOnUse"
                width="6" height="6" patternTransform="rotate(45)">
                <rect width="6" height="6" fill={s.color} fillOpacity="0.35" />
                <line x1="0" y1="0" x2="0" y2="6" stroke={s.color} strokeWidth="2" />
              </pattern>
            </defs>
          ))}
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick}
            axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
          <YAxis tickFormatter={kFmt} tick={axisTick} axisLine={false} tickLine={false} width={52} />
          <Tooltip cursor={barCursor}
            content={<CcyTooltip labelFormatter={longMonth} currency={currency} colorMap={colorMap} />} />
          {showLegend && <Legend {...legendProps} />}
          {series.map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.name} stackId="m"
              fill={s.hatch ? `url(#fxhatch-${s.key})` : s.color}
              maxBarSize={28} isAnimationActive={false}
              radius={i === series.length - 1 ? [3, 3, 0, 0] : 0} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Díjtípus-összesítő (vízszintes, rendezett) ─────────────────────────────
// A bank-fees-summary `monthly` sorai nem hordoznak fee_type-ot, ezért a
// díjtípus-nézet az időszaki by_fee_type összesítés rendezett sávdiagramja.
// data: [{ name, value, count?, color }] – érték szerint csökkenő.
export function FeeTypeTotalsChart({ data = [], currency = 'HUF' }) {
  if (!data.length) return EMPTY('Nincs adat erre az időszakra.');
  const h = data.length * 34 + 20;
  const dec = currency === 'HUF' ? 0 : 2;
  const unit = currency === 'HUF' ? 'Ft' : currency;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart layout="vertical" data={data} barCategoryGap="28%"
        margin={{ top: 4, right: 110, left: 0, bottom: 4 }}>
        <CartesianGrid {...gridProps} vertical={true} horizontal={false} />
        <XAxis type="number" tickFormatter={kFmt} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
        <YAxis type="category" dataKey="name" width={120}
          tick={{ fontSize: 12.5, fill: 'var(--text)' }}
          tickLine={false} axisLine={false} interval={0} />
        <Tooltip cursor={barCursor} content={<CcyTooltip currency={currency} />} />
        <Bar dataKey="value" name="Összeg" radius={[0, 4, 4, 0]} maxBarSize={18}
          isAnimationActive={false}>
          {data.map((d) => <Cell key={d.name} fill={d.color} />)}
          <LabelList dataKey="value" position="right"
            formatter={(v) => `${huFmt(v, dec)} ${unit}`}
            style={{ fontSize: 11.5, fontFamily: 'var(--mono)', fill: 'var(--text-muted)' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Effektív árfolyam vs MNB-közép (vonaldiagram) ──────────────────────────
// Páronként EGY tömör vonal (deviza-stabil szín) + szaggatott szürke
// MNB-közép benchmark (kötelező kódolás: szaggatott = viszonyítási alap).
// Közös y-skála; pontok tooltippel. rows: [{month, pair, effective_rate, mnb_mid}]
export function FxEffChart({ rows = [], pairColor }) {
  if (!rows.length) return EMPTY('Nincs adat.');
  const pairs = [...new Set(rows.map((r) => r.pair))];
  const months = [...new Set(rows.map((r) => r.month))].sort();
  const byKey = {};
  rows.forEach((r) => { byKey[`${r.month}|${r.pair}`] = r; });
  const data = months.map((m) => {
    const row = { month: m };
    pairs.forEach((p, i) => {
      const r = byKey[`${m}|${p}`];
      row[`eff${i}`] = r ? r.effective_rate : null;
      row[`mid${i}`] = r ? r.mnb_mid : null;
    });
    return row;
  });
  const colorMap = {};
  pairs.forEach((p, i) => {
    colorMap[`eff${i}`] = pairColor(p, i);
    colorMap[`mid${i}`] = 'var(--text-muted)';
  });
  return (
    <div>
      <AxisTitle>HUF / 1 deviza</AxisTitle>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick}
            axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
          <YAxis domain={[(min) => min * 0.998, (max) => max * 1.002]}
            tickFormatter={(v) => Number(v).toFixed(1)} tickCount={4}
            tick={axisTick} axisLine={false} tickLine={false} width={52} />
          <Tooltip content={<RateTooltip colorMap={colorMap} />} />
          {pairs.map((p, i) => (
            <Line key={`mid${i}`} dataKey={`mid${i}`} name={`MNB-közép (${p})`}
              stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="5 4"
              dot={false} connectNulls isAnimationActive={false} />
          ))}
          {pairs.map((p, i) => (
            <Line key={`eff${i}`} dataKey={`eff${i}`} name={`${p} effektív`}
              stroke={colorMap[`eff${i}`]} strokeWidth={2.2}
              dot={{ r: 3.2, fill: colorMap[`eff${i}`], strokeWidth: 0 }}
              connectNulls isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Havi kiadás-hisztogram (pre-funding) ───────────────────────────────────
// Kötelező kódolás: HÁROM szaggatott referenciavonal (medián szürke, P90 kék,
// max piros, jobb szélen címkézve) + a P90 fölötti hónapok kiemelt kitöltése.
// rows: [{ month, spend, median, p90, max }] – egy devizára szűrve.
export function FxHistChart({ rows = [], ccy }) {
  if (!rows.length) return EMPTY('Nincs adat.');
  const med = rows[0].median, p90 = rows[0].p90, mx = rows[0].max;
  const refLabel = (value, fill) => ({
    value, fill, position: 'insideTopRight', fontSize: 10.5, fontFamily: 'var(--mono)',
  });
  return (
    <div>
      <AxisTitle>{ccy}</AxisTitle>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={rows} barCategoryGap="34%" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick}
            axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
          <YAxis domain={[0, mx * 1.12]} tickFormatter={kFmt}
            tick={axisTick} axisLine={false} tickLine={false} width={52} />
          <Tooltip cursor={barCursor}
            content={<CcyTooltip labelFormatter={longMonth} currency={ccy}
              colorMap={{ spend: 'var(--primary)' }} />} />
          <Bar dataKey="spend" name="Havi kiadás" maxBarSize={28}
            radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {rows.map((r, i) => (
              <Cell key={i} fill={r.spend > p90 ? 'var(--primary)' : '#334867'}
                fillOpacity={r.spend > p90 ? 0.9 : 0.7} />
            ))}
          </Bar>
          <ReferenceLine y={med} stroke="var(--text-muted)" strokeWidth={1.3}
            strokeDasharray="5 4" label={refLabel('Medián', 'var(--text-muted)')} />
          <ReferenceLine y={p90} stroke="var(--primary)" strokeWidth={1.3}
            strokeDasharray="5 4" label={refLabel('P90', 'var(--primary)')} />
          <ReferenceLine y={mx} stroke="var(--danger)" strokeWidth={1.3}
            strokeDasharray="2 3" label={refLabel('Max', 'var(--danger)')} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Break-even törtvonal (csomag-szimuláció) ───────────────────────────────
// Csomagonként egy vonal (a díjgörbe töréspontja a keret-határnál – a
// paramétereket a backend plan-configja hajtja, nem hardcode), függőleges
// break-even referenciavonal, a tényhónapok FX-volumene pont-sorként az
// alapvonalon. months: [{fx_volume_huf}], plans: [{plan,label,monthly_fee,
// fx_free_allowance_month,fx_fee_pct_over}]
export function FxBreakEvenChart({ months = [], plans = [], breakEven, planColor }) {
  if (!plans.length) return EMPTY('Nincs adat.');
  const feeAt = (p, V) => p.monthly_fee + Math.max(0, V - p.fx_free_allowance_month) * p.fx_fee_pct_over;
  const maxAllow = Math.max(...plans.map((p) => p.fx_free_allowance_month), 1);
  const Vmax = Math.max(maxAllow * 1.25, (breakEven || 0) * 1.6, 25e6);
  const Ymax = Math.max(...plans.map((p) => feeAt(p, Vmax)), 1) * 1.1;
  // Töréspontos x-rács: 0 + keret-határok + break-even + Vmax (lineáris szakaszok).
  const xs = [...new Set([0, ...plans.map((p) => p.fx_free_allowance_month),
    ...(breakEven ? [breakEven] : []), Vmax])]
    .filter((v) => v >= 0 && v <= Vmax).sort((a, b) => a - b);
  const data = xs.map((v) => {
    const row = { v };
    plans.forEach((p) => { row[p.plan] = feeAt(p, v); });
    return row;
  });
  const pts = months.map((m) => ({ v: m.fx_volume_huf, y: 0 }));
  const ticks = [0, .2, .4, .6, .8, 1].map((f) => Math.round(Vmax * f));
  const colorMap = Object.fromEntries(plans.map((p) => [p.plan, planColor(p)]));
  return (
    <div>
      <AxisTitle>Ft/hó</AxisTitle>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="v" type="number" domain={[0, Vmax]} ticks={ticks}
            tickFormatter={kFmt} tick={axisTick}
            axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
          <YAxis domain={[0, Ymax]} tickFormatter={kFmt}
            tick={axisTick} axisLine={false} tickLine={false} width={52} />
          <Tooltip content={<CcyTooltip colorMap={colorMap}
            labelFormatter={(v) => `FX-volumen: ${huFmt(v)} Ft/hó`} />} />
          {plans.map((p) => (
            <Line key={p.plan} dataKey={p.plan} type="linear"
              name={(p.label || p.plan).replace('Revolut Business – ', '')}
              stroke={planColor(p)} strokeWidth={2.4} dot={false}
              isAnimationActive={false} />
          ))}
          {breakEven && (
            <ReferenceLine x={breakEven} stroke="var(--text-muted)" strokeDasharray="4 3"
              label={({ viewBox }) => (
                <text x={viewBox.x + 4} y={viewBox.y + 13} fontSize={10.5}
                  fill="var(--text-muted)" fontFamily="var(--mono)">break-even</text>
              )} />
          )}
          <Scatter data={pts} dataKey="y" name="Tényhónapok" fill="#6b7a90"
            legendType="none" tooltipType="none" isAnimationActive={false}
            shape={(props) => <circle cx={props.cx} cy={props.cy} r={2.4} fill="#6b7a90" />} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Havi díjkülönbség (Grow − Scale) – nulla körüli diverging oszlopok ─────
// rows: [{ month, delta }] · delta < 0 → a Grow olcsóbb (zöld), delta > 0 →
// a Scale olcsóbb (kék) – a csomag-stabil színekkel egyező kódolás.
export function PlanDeltaChart({ rows = [], growColor = '#22c55e', scaleColor = '#5b8def' }) {
  if (!rows.length) return EMPTY('Nincs adat.');
  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={rows} barCategoryGap="34%" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
        <YAxis tickFormatter={kFmt} tick={axisTick} axisLine={false} tickLine={false} width={52} />
        <Tooltip cursor={barCursor} content={<CcyTooltip labelFormatter={longMonth} signed />} />
        <ReferenceLine y={0} stroke="var(--text-muted)" strokeDasharray="4 3" />
        <Bar dataKey="delta" name="Grow − Scale" maxBarSize={24}
          radius={[2, 2, 0, 0]} isAnimationActive={false}>
          {rows.map((r, i) => <Cell key={i} fill={r.delta < 0 ? growColor : scaleColor} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
